import React, { useState } from "react";
import { Gauge, Calculator, TrendingUp } from "lucide-react";

const Card = ({ children }) => <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">{children}</div>;
const Field = ({ label, ...p }) => (
  <div>
    <label className="block text-sm font-semibold text-navy mb-1">{label}</label>
    <input className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-cool focus:outline-none" {...p} />
  </div>
);
const Stat = ({ label, value, dark }) => (
  <div className={`rounded-xl p-5 ${dark ? "bg-navy text-white" : "bg-slate-50"}`}>
    <div className={`text-3xl font-extrabold ${dark ? "text-launch" : "text-navy"}`}>{value}</div>
    <div className={`text-sm mt-1 ${dark ? "text-slate-300" : "text-slate-500"}`}>{label}</div>
  </div>
);
const num = (v) => parseFloat(v) || 0;

export function LoadCalc() {
  const [sqft, setSqft] = useState("");
  const [insulation, setInsulation] = useState("average");
  const [sun, setSun] = useState("average");
  const [occupants, setOccupants] = useState("");
  const [windows, setWindows] = useState("");

  const factor = { poor: 32, average: 27, good: 22 }[insulation];
  const sunAdj = { shaded: -1.5, average: 0, sunny: 2 }[sun];
  const btu = Math.round(num(sqft) * (factor + sunAdj) + num(occupants) * 600 + num(windows) * 800);
  const tons = btu ? (btu / 12000).toFixed(1) : "0";

  return (
    <div>
      <div className="flex items-center gap-2 mb-1"><Gauge className="w-5 h-5 text-cool" /><h1 className="text-2xl font-bold text-navy">Load Calc (Manual J Estimate)</h1></div>
      <p className="text-slate-500 mb-5 text-sm">Quick residential cooling-load estimate for Florida homes. Always confirm with a full Manual J before quoting.</p>
      <Card>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Conditioned Area (sq ft)" type="number" value={sqft} onChange={(e) => setSqft(e.target.value)} data-testid="lc-sqft" placeholder="e.g. 1800" />
          <Field label="Occupants" type="number" value={occupants} onChange={(e) => setOccupants(e.target.value)} placeholder="e.g. 4" />
          <Field label="# of Windows" type="number" value={windows} onChange={(e) => setWindows(e.target.value)} placeholder="e.g. 10" />
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Insulation</label>
            <select value={insulation} onChange={(e) => setInsulation(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"><option value="poor">Poor</option><option value="average">Average</option><option value="good">Good</option></select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1">Sun Exposure</label>
            <select value={sun} onChange={(e) => setSun(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"><option value="shaded">Shaded</option><option value="average">Average</option><option value="sunny">Very Sunny</option></select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-6">
          <Stat label="Estimated Cooling Load" value={`${btu.toLocaleString()} BTU`} />
          <Stat label="Suggested System Size" value={`${tons} tons`} dark />
        </div>
      </Card>
    </div>
  );
}

export function LaborCalc() {
  const [rate, setRate] = useState("");
  const [hours, setHours] = useState("");
  const [techs, setTechs] = useState("1");
  const [markup, setMarkup] = useState("40");

  const cost = num(rate) * num(hours) * num(techs);
  const price = cost * (1 + num(markup) / 100);

  return (
    <div>
      <div className="flex items-center gap-2 mb-1"><Calculator className="w-5 h-5 text-cool" /><h1 className="text-2xl font-bold text-navy">Labor Calculator</h1></div>
      <p className="text-slate-500 mb-5 text-sm">Price a job's labor with your target markup.</p>
      <Card>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Hourly Rate ($)" type="number" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="labor-rate" placeholder="e.g. 95" />
          <Field label="Hours" type="number" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="e.g. 6" />
          <Field label="# of Technicians" type="number" value={techs} onChange={(e) => setTechs(e.target.value)} />
          <Field label="Markup (%)" type="number" value={markup} onChange={(e) => setMarkup(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-6">
          <Stat label="Labor Cost" value={`$${cost.toFixed(2)}`} />
          <Stat label="Price to Customer" value={`$${price.toFixed(2)}`} dark />
        </div>
      </Card>
    </div>
  );
}

export function MarginsCalc() {
  const [revenue, setRevenue] = useState("");
  const [parts, setParts] = useState("");
  const [labor, setLabor] = useState("");
  const [overhead, setOverhead] = useState("");

  const cost = num(parts) + num(labor) + num(overhead);
  const profit = num(revenue) - cost;
  const margin = num(revenue) ? ((profit / num(revenue)) * 100).toFixed(1) : "0";

  return (
    <div>
      <div className="flex items-center gap-2 mb-1"><TrendingUp className="w-5 h-5 text-cool" /><h1 className="text-2xl font-bold text-navy">Margins</h1></div>
      <p className="text-slate-500 mb-5 text-sm">Check a job's gross profit and margin before you commit.</p>
      <Card>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Job Revenue ($)" type="number" value={revenue} onChange={(e) => setRevenue(e.target.value)} data-testid="margin-revenue" placeholder="e.g. 6500" />
          <Field label="Parts Cost ($)" type="number" value={parts} onChange={(e) => setParts(e.target.value)} placeholder="e.g. 2200" />
          <Field label="Labor Cost ($)" type="number" value={labor} onChange={(e) => setLabor(e.target.value)} placeholder="e.g. 900" />
          <Field label="Overhead ($)" type="number" value={overhead} onChange={(e) => setOverhead(e.target.value)} placeholder="e.g. 400" />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-6">
          <Stat label="Gross Profit" value={`$${profit.toFixed(2)}`} />
          <Stat label="Gross Margin" value={`${margin}%`} dark />
        </div>
      </Card>
    </div>
  );
}
